/**
 * Die E-Mail, mit der ein Beleg das Haus verlässt.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Warum die Texte hierher gehören und nicht in die Route
 * ════════════════════════════════════════════════════════════════════════════
 *
 * Weil der Betrieb sie vor dem Versand sehen und ändern soll. Ein Text, der in
 * einer Serverroute als Zeichenkette zusammengesetzt wird, ist für die
 * Oberfläche unerreichbar – und genau deshalb gab es bisher keinen Editor: Es
 * gab nichts anzuzeigen, bis die Nachricht schon unterwegs war.
 *
 * Jetzt liegt die Vorlage an einer Stelle, die beide Seiten lesen können. Der
 * Server schlägt vor, der Mensch entscheidet, und versendet wird, was im Feld
 * steht.
 *
 * ════════════════════════════════════════════════════════════════════════════
 * Platzhalter
 * ════════════════════════════════════════════════════════════════════════════
 *
 * `{{rechnungsnummer}}`, `{{offertennummer}}`, `{{firmenname}}`,
 * `{{kundenname}}`. Sie werden gefüllt, BEVOR die Nachricht im Editor
 * erscheint – der Benutzer sieht die fertige Mail und keine geschweiften
 * Klammern. `pruefeOhnePlatzhalter` ist der Riegel davor, dass doch eine
 * hinausgeht: Ein Kunde, der «Ihre Rechnung {{rechnungsnummer}}» bekommt,
 * sieht eine kaputte Software, und das zu Recht.
 */

export type Belegart = 'rechnung' | 'offerte'

export interface Vorlagenwerte {
  nummer: string
  firmenname: string
  kundenname: string
}

export interface Mailvorlage {
  betreff: string
  text: string
}

/*
  Die Vorlagen stehen hier wörtlich, mit Leerzeilen genau dort, wo sie in der
  Nachricht erscheinen sollen. Keine Zusammensetzung aus Satzteilen: Wer den
  Text ändern will, soll ihn lesen können wie er ankommt.
*/
export const VORLAGE_RECHNUNG: Mailvorlage = {
  betreff: 'Ihre Rechnung {{rechnungsnummer}} – {{firmenname}}',
  text: `Guten Tag

Vielen Dank für Ihr Vertrauen.

Im Anhang erhalten Sie unsere Rechnung {{rechnungsnummer}} als PDF. Die Rechnungsdetails sowie alle Zahlungsinformationen finden Sie im beigefügten Dokument.

Bei Fragen stehen wir Ihnen gerne zur Verfügung.

Freundliche Grüsse

{{firmenname}}`
}

export const VORLAGE_OFFERTE: Mailvorlage = {
  betreff: 'Ihre Offerte {{offertennummer}} – {{firmenname}}',
  text: `Guten Tag

Vielen Dank für Ihre Anfrage und Ihr Interesse an unseren Leistungen.

Gerne senden wir Ihnen unsere Offerte {{offertennummer}} als PDF zu.

Bei Fragen oder Anpassungswünschen stehen wir Ihnen gerne zur Verfügung. Wir freuen uns auf Ihre Rückmeldung.

Freundliche Grüsse

{{firmenname}}`
}

/**
 * Die Vorlage für den Weg «an mich selbst, zum Weiterleiten».
 *
 * Sie bleibt kurz und sachlich: Empfänger ist der Betrieb selbst, nicht der
 * Kunde. Hier steht bewusst nicht «Guten Tag, vielen Dank für Ihr Vertrauen» –
 * das wäre ein Betrieb, der sich selbst dankt.
 */
export const VORLAGE_WEITERLEITUNG: Mailvorlage = {
  betreff: 'Zum Weiterleiten: {{rechnungsnummer}} – {{kundenname}}',
  text: `Im Anhang liegt der Beleg {{rechnungsnummer}} für {{kundenname}} als PDF.

Du kannst ihn von hier aus selbst an den Kunden weiterleiten.`
}

const MUSTER = /\{\{\s*([a-zA-Z]+)\s*\}\}/g

/** Platzhalter ersetzen. Unbekannte bleiben stehen und fallen damit auf. */
export function fuelleVorlage(v: Mailvorlage, w: Vorlagenwerte): Mailvorlage {
  const ersetze = (s: string): string =>
    s.replace(MUSTER, (ganz, name: string) => {
      switch (name) {
        case 'rechnungsnummer':
        case 'offertennummer':
        case 'belegnummer':
          return w.nummer
        case 'firmenname':
          return w.firmenname
        case 'kundenname':
          return w.kundenname
        default:
          return ganz
      }
    })
  return { betreff: ersetze(v.betreff), text: ersetze(v.text) }
}

/** Die Vorlage für diese Belegart und diesen Weg. */
export function vorlageFuer(art: Belegart, weg: 'kunde' | 'firma' | 'benutzer'): Mailvorlage {
  if (weg !== 'kunde') return VORLAGE_WEITERLEITUNG
  return art === 'offerte' ? VORLAGE_OFFERTE : VORLAGE_RECHNUNG
}

/**
 * Riegel: keine technischen Platzhalter in einer versendeten Nachricht.
 *
 * Greift auch für von Hand geänderte Texte – wer eine Klammer stehen lässt,
 * bekommt eine Meldung und nicht einen Kunden, der sie liest.
 */
export function offenePlatzhalter(s: string): string[] {
  return [...s.matchAll(MUSTER)].map((m) => m[0])
}
